import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, AlertCircle } from '../components/Icons';
import { API, useAuth } from '../contexts/AuthContext';
import { type Currency, CURRENCY_SYMBOL } from '../lib/currency';

type Cadence = 'monthly' | 'annual';
type SkuKind = 'topup' | 'subscription' | 'free';

interface DisplayPrice { INR: string; USD: string; }
interface Sku {
  id: string; kind: SkuKind; credits: string;
  cadence: Cadence | null; displayPrice: DisplayPrice;
}
interface PricingResponse {
  currency: Currency | null; lockedCurrency: Currency | null; skus: Sku[];
}

function planNameFromSkuId(id: string): string {
  const tier = (id.split(/[_-]/)[0] ?? id).toLowerCase();
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function parsePrice(priceStr: string): number {
  return parseFloat(priceStr.replace(/,/g, ''));
}

function perCreditLabel(priceStr: string, credits: string, currency: Currency): string {
  const price = parsePrice(priceStr);
  const creds = parseInt(credits, 10);
  if (!isFinite(price) || !creds) return '';
  const sym = CURRENCY_SYMBOL[currency];
  const per = price / creds;
  return `≈ ${sym}${per.toFixed(2)} per credit`;
}

function PublicPricing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [pricingData, setPricingData]           = useState<PricingResponse | null>(null);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>('INR');
  const [cadence, setCadence]                   = useState<Cadence>('monthly');

  useEffect(() => {
    axios.get<PricingResponse>(`${API}/pricing`)
      .then(res => {
        setPricingData(res.data);
        const defaultCurrency = res.data.lockedCurrency ?? res.data.currency;
        if (defaultCurrency) setSelectedCurrency(defaultCurrency);
      })
      .catch(() => setError('Failed to load plans. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  const { topupSkus, subscriptionSkus, hasAnySubscription, baseSubSku, bestValueIdx } = useMemo(() => {
    const skus    = pricingData?.skus ?? [];
    const allSubs = skus.filter(s => s.kind === 'subscription');
    const topups  = skus.filter(s => s.kind === 'topup');

    // find topup with lowest per-credit cost → "best value"
    let bestIdx = -1;
    let bestRate = Infinity;
    topups.forEach((s, i) => {
      const price = parsePrice(s.displayPrice[selectedCurrency]);
      const creds = parseInt(s.credits, 10);
      if (creds > 0 && price / creds < bestRate) { bestRate = price / creds; bestIdx = i; }
    });

    return {
      topupSkus:          topups,
      subscriptionSkus:   allSubs.filter(s => s.cadence === cadence),
      hasAnySubscription: allSubs.length > 0,
      baseSubSku:         allSubs[0] ?? null,   // fallback for name/badge when cadence has no SKUs
      bestValueIdx:       bestIdx,
    };
  }, [pricingData, cadence, selectedCurrency]);

  const periodLabel = cadence === 'annual' ? '/year' : '/month';
  const billingNote = cadence === 'annual' ? 'Billed annually · cancel anytime' : 'Billed monthly · cancel anytime';

  return (
    <div className="page-root">
      <header className="app-header">
        <div className="header-inner">
          <button className="header-action-btn" onClick={() => navigate(-1)} aria-label="Back">
            <ArrowLeft size={20} />
          </button>
        </div>
      </header>

      <div className="page-scroll">
        <div className="pub-pricing-wrap">

          {/* ── Hero ─────────────────────────────────────────── */}
          <div className="pub-hero">
            <span className="pub-hero-badge">FREE TO START</span>
            <h1 className="pub-hero-title">5 free credits<br />when you sign up.</h1>
            <p className="pub-hero-sub">No credit card required · start visualising instantly</p>
          </div>

          <div className="pub-hero-divider" />

          {error && (
            <div className="alert alert-error" role="alert" style={{ margin: '0 0 16px' }}>
              <AlertCircle size={16} style={{ flexShrink: 0 }} />
              <span>{error}</span>
            </div>
          )}

          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
              <div className="spinner" />
            </div>
          )}

          {!loading && !error && pricingData && (
            <>
              {/* ── Plans header row ─────────────────────────── */}
              <div className="pub-section-header">
                <div>
                  <h2 className="pub-section-title">Credit plans</h2>
                  <p className="pub-section-sub">Cancel anytime · credits roll over for 30 days</p>
                </div>
                <div className="plans-toggle-group" style={{ flexShrink: 0 }}>
                  {(['INR', 'USD'] as Currency[]).map(c => (
                    <button
                      key={c}
                      type="button"
                      className={`plans-toggle-btn${selectedCurrency === c ? ' active' : ''}`}
                      onClick={() => setSelectedCurrency(c)}
                    >{c}</button>
                  ))}
                </div>
              </div>

              {/* ── Subscription card ───────────────────────── */}
              {hasAnySubscription && (() => {
                const displaySku = subscriptionSkus[0] ?? baseSubSku!;
                const priceNum = displaySku ? parsePrice(displaySku.displayPrice[selectedCurrency]) : NaN;
                const sym = CURRENCY_SYMBOL[selectedCurrency];
                return (
                  <div className="pub-plan-card">
                    <div className="pub-plan-top-row">
                      <div className="pub-plan-name-row">
                        <span className="pub-plan-name">{planNameFromSkuId(displaySku.id)}</span>
                        <span className="plan-popular-badge-inline">POPULAR</span>
                      </div>
                      <div className="plans-toggle-group">
                        <button
                          type="button"
                          className={`plans-toggle-btn${cadence === 'monthly' ? ' active' : ''}`}
                          onClick={() => setCadence('monthly')}
                        >Monthly</button>
                        <button
                          type="button"
                          className={`plans-toggle-btn${cadence === 'annual' ? ' active' : ''}`}
                          onClick={() => setCadence('annual')}
                        >Annual <span className="plans-toggle-save">−20%</span></button>
                      </div>
                    </div>

                    {subscriptionSkus.length > 0 ? (
                      <>
                        <p className="pub-plan-credits">{displaySku.credits} credits / month</p>
                        <div className="pub-plan-price-row">
                          <span className="pub-plan-price-big">{sym}{isFinite(priceNum) ? priceNum.toLocaleString(selectedCurrency === 'INR' ? 'en-IN' : 'en-US', { maximumFractionDigits: 0 }) : displaySku.displayPrice[selectedCurrency]}</span>
                          <span className="pub-plan-price-period">{periodLabel}</span>
                        </div>
                        <p className="pub-plan-billing">{billingNote}</p>
                      </>
                    ) : (
                      <p className="pub-plan-billing" style={{ paddingTop: 8 }}>Annual plans coming soon</p>
                    )}
                  </div>
                );
              })()}

              {/* ── Top-up packs ─────────────────────────────── */}
              {topupSkus.length > 0 && (
                <>
                  <div className="pub-topup-divider">
                    <span>TOP UP CREDITS · ONE-TIME · NEVER EXPIRE</span>
                  </div>
                  <div className="pub-packs-list">
                    {topupSkus.map((sku, idx) => {
                      const priceStr = sku.displayPrice[selectedCurrency];
                      const sym      = CURRENCY_SYMBOL[selectedCurrency];
                      const priceNum = parsePrice(priceStr);
                      const isBest   = idx === bestValueIdx;
                      return (
                        <div key={sku.id} className="pub-pack-row">
                          <div className="pub-pack-info">
                            <div className="pub-pack-name">
                              <span className="pub-pack-bullet">•</span>
                              <span>{sku.credits} credits</span>
                            </div>
                            <p className="pub-pack-per-credit">
                              {perCreditLabel(priceStr, sku.credits, selectedCurrency)}
                              {isBest && <span className="pub-pack-best"> · best value</span>}
                            </p>
                          </div>
                          <span className="pub-pack-price">
                            {sym}{isFinite(priceNum) ? priceNum.toLocaleString(selectedCurrency === 'INR' ? 'en-IN' : 'en-US', { maximumFractionDigits: 0 }) : priceStr}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              <div style={{ height: 100 }} />
            </>
          )}
        </div>
      </div>

      {/* ── Sticky bottom CTA ───────────────────────────────── */}
      <div className="plans-sticky-cta">
        <div className="plans-sticky-cta-inner">
          {user ? (
            <button type="button" className="plans-cta-btn" onClick={() => navigate('/credits')}>
              <span className="plans-cta-label">Manage my credits</span>
              <span className="plans-cta-price">→</span>
            </button>
          ) : (
            <>
              <button type="button" className="plans-cta-btn" onClick={() => navigate('/login')}>
                <span className="plans-cta-label">Sign up for 5 free credits</span>
                <span className="plans-cta-price">→</span>
              </button>
              <p className="plans-cta-secured">
                Already have an account?{' '}
                <button type="button" className="public-pricing-signin-link" onClick={() => navigate('/login')}>
                  Sign in
                </button>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PublicPricing;
